<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <div
    :id="`product-gallery-carousel-${type}`"
    ref="splide"
    class="product-gallery-carousel splide"
    :class="`product-gallery-carousel--${namespace} ${loading ? [$class('loading')] : ''}`"
    :aria-label="ariaLabel"
  >
    <div :class="`product-gallery-carousel__${type}--arrow-container`">
      <div
        v-if="type === 'thumbnails'"
        class="splide__arrows splide__arrows--ltr"
      >
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
          :aria-label="$string('accessibility.carousel.previous')"
          aria-controls="splide01-track"
        >
          <icon-chevron-up />
        </button>
        <button
          class="splide__arrow splide__arrow--next"
          type="button"
          :aria-label="$string('accessibility.carousel.next')"
          aria-controls="splide01-track"
        >
          <icon-chevron-down />
        </button>
      </div>
      <div class="splide__track">
        <div class="splide__list">
          <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
          <div
            v-for="(mediaItem, index) of media"
            :key="`media-${product.handle}-${index}`"
            class="splide__slide"
            :tabindex="type === 'thumbnails' ? '0' : '-1'"
            :role="type === 'thumbnails' ? 'button' : 'img'"
            :aria-label="
              type === 'thumbnails'
                ? $string('product.gallery.thumbnail', {
                    replace: { number: index + 1 },
                  })
                : null
            "
            @click.prevent="
              type === 'thumbnails'
                ? handleImageClick(mediaItem.index)
                : undefined
            "
            @keydown.enter.prevent="
              type === 'thumbnails'
                ? handleImageClick(mediaItem.index)
                : undefined
            "
            @keydown.space.prevent="
              type === 'thumbnails'
                ? handleImageClick(mediaItem.index)
                : undefined
            "
          >
            <button
              v-if="type !== 'thumbnails' && isMobile && media.length > 1"
              class="product-gallery__zoom"
              type="button"
              @click="zoomGallery"
            >
              <icon-plus v-if="!isZoomed" />
              <icon-minus v-else />

              <span
                class="visually-hidden"
                v-text="$string('product.gallery.zoom')"
              />
            </button>
            <div
              v-if="type !== 'thumbnails' && mediaItem.mediaType === 'video'"
              class="product-gallery-carousel__media-container"
              :class="{
                [$class('loading')]: loading,
              }"
            >
              <video-player
                class="product-gallery-carousel__video"
                :attributes="{ autoplay: false }"
                :controls="{
                  duration: true,
                  id: `gallery-controls-${mediaItem.index}`,
                  playPause: true,
                  progressBar: true,
                  volume: true,
                }"
                :image="mediaItem.previewImage"
                :video="getVideoSource(mediaItem.sources)"
                :sizes="sizes"
                @video-state-update="handleVideoStateUpdate"
              />

              <responsive-image
                v-if="type !== 'modal'"
                class="product-gallery-carousel__video-spacer"
                :image="{}"
                :ratio="ratio"
                :sizes="sizes"
                :padding="false"
                :widths="widths"
              />
            </div>

            <div
              v-else
              class="product-gallery-carousel__image-container"
              :class="{
                [$class('loading')]:
                  loading && (type === 'thumbnails' || index > 0),
              }"
            >
              <responsive-image
                class="product-gallery-carousel__image"
                :image="mediaItem.previewImage || mediaItem.image || mediaItem"
                :lazyload="type === 'thumbnails' ? index > 7 : index > 0"
                :loaded-animation="type === 'thumbnails' || index > 0"
                :ratio="ratio"
                :sizes="sizes"
                :widths="widths"
              />

              <icon-play
                v-if="type === 'thumbnails' && mediaItem.mediaType === 'video'"
                class="product-gallery-carousel__play-icon"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product gallery carousel (product-gallery-carousel)
 * -----------------------------------------------------------------------------
 * Splide powered carousel shared between gallery and modal.
 *
 * @param {Object} config - Splide carousel config.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namesoace.
 * @param {String} [layout] - Current layout.
 * @param {Boolean} [loading] - Loading state.
 * @param {Array} media - Media gallery for current variant and product.
 * @param {String} namespace - Namespace, either `gallery` or `modal`.
 * gallery modal to save file size of main product.
 * @param {Object} product - Local product object.
 * @param {String} ratio - Ratio attribute.
 * @param {String} sizes - Sizes attribute.
 * @param {String} type - Type of carousel, `main`, `thumbnails`, or `modal`.
 * @param {String} widths - Image widths.
 *
 * @emits updateCurrentIndex -  Emit update current carousel index event.
 *
 */
// eslint-disable-next-line import/named
import { Splide } from '@splidejs/splide';
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import useComponentsStore from '~stores/components';
import useIndexStore from '~stores/index';

import ResponsiveImage from '~global/images/responsive-image';

import IconPlus from '~icons/directional-navigation/plus.svg';
import IconMinus from '~icons/directional-navigation/minus.svg';
import IconPlay from '~icons/general/play.svg';
import IconChevronUp from '~icons/directional-navigation/chevron/up.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';

export default {
  name: 'ProductGalleryCarousel',

  components: {
    IconMinus,
    IconPlay,
    IconPlus,
    IconChevronDown,
    IconChevronUp,
    ResponsiveImage,
    VideoPlayer: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.video-player' */
          '~async/video-player/video-player'
        ),
    }),
  },

  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    currentIndex: {
      type: Number,
      default: 0,
      required: true,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      required: true,
    },
    layout: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    media: {
      type: Array,
      default: () => [],
      required: true,
    },
    namespace: {
      type: String,
      default: 'gallery',
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        title: '',
      }),
      required: true,
    },
    ratio: {
      type: String,
      default: '1:1',
    },
    sizes: {
      type: String,
      default: '100vw',
    },
    type: {
      type: String,
      default: 'main',
    },
    widths: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localSlideIndex: 0,
      carousel: null,
      isZoomed: false,
      isProgrammaticMove: false,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
      getRtlWritingDirection: 'getRtlWritingDirection',
    }),

    /**
     * Compute if media array has video.
     * @returns {Boolean}
     */
    hasVideo() {
      return this.media.some((mediaItem) => {
        return this.type !== 'thumbnails' && mediaItem.mediaType === 'video';
      });
    },

    ariaLabel() {
      if (this.type === 'thumbnails') {
        return this.$string('product.thumbnails.label', {
          replace: { product: this.product.title },
        });
      }
      return this.$string('product.gallery.label', {
        replace: { product: this.product.title },
      });
    },

    /**
     * Compute if current gallery namespace is active.
     * @returns {Boolean}
     */
    namespaceActive() {
      return this.currentNamespace === this.namespace;
    },
  },

  watch: {
    /**
     * Watch current media index to update slide index whilst active.
     * - This is used to show variant media.
     */
    currentIndex() {
      if (!this.namespaceActive || this.currentIndex === this.localSlideIndex) {
        return;
      }

      this.goToIndex(this.currentIndex, true);
    },

    /**
     * Watch the media as sometimes the video or variant image come in delayed
     * - Reinitializes the carousel
     */
    media: {
      async handler(newValue, oldValue) {
        if (oldValue.length !== newValue.length) {
          await this.carousel.destroy();
          await this.mountCarousel();
        }
      },
      deep: true,
    },

    /**
     * Watch has video to load video player when required.
     */
    hasVideo() {
      this.loadVideoPlayer();
    },

    /**
     * Watch is mobile to force refresh the carousel.
     * - Fixes issue where switching from grid to other layout broke the arrows
     *   and pagination.
     */
    async isMobile() {
      if (
        this.namespace !== 'gallery' ||
        !this.isMobile ||
        this.layout === 'grid'
      ) {
        return;
      }

      await this.$nextTick();
      this.goToIndex(0);
    },

    /**
     * Watch namespace active to update visible slide.
     */
    async namespaceActive() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex);
      }
    },

    /**
     * Watch product to refresh gallery position.
     */
    async product() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex);
      }
    },

    /**
     * Watches the ariaLabel to restore it when it's gone
     */
    ariaLabel() {
      this.restoreAriaLabel();
    },
  },

  mounted() {
    this.loadVideoPlayer();
    this.mountCarousel();
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useComponentsStore, {
      loadComponent: 'load',
    }),

    async mountCarousel() {
      if (!this.$refs.splide) {
        return;
      }

      this.carousel = new Splide(this.$refs.splide, {
        direction: this.getRtlWritingDirection ? 'rtl' : 'ltr',
        keyboard: true,
        flickPower: 200,
        flickMaxPages: 1,
        mediaQuery: 'min',
        speed: this.$timing('normal'),
        start: this.currentIndex || 0,
        type: 'slide',
        trimSpace: true,
        waitForTransition: true,
        ...this.config,
      });

      this.carousel.on('mounted', () => {
        this.handleSplideMounted();
      });

      // add aria labels and remove orientation after creation
      this.carousel.on('updated', () => {
        this.removeAriaOrientation();
        this.restoreAriaLabel();
      });

      // track slide changes to keep localSlideIndex in sync
      this.carousel.on('moved', (newIndex) => {
        // sync the main carousel when the thumbnails move via arrows/drag
        // rather than a direct thumbnail click (which emits separately)
        if (
          this.type === 'thumbnails' &&
          this.namespaceActive &&
          !this.isProgrammaticMove
        ) {
          this.$emit('update-current-index', newIndex);
        }

        this.localSlideIndex = newIndex;
        this.isProgrammaticMove = false;
      });

      await this.$nextTick();
      this.carousel.mount();
    },

    /**
     * Load video player component.
     */
    loadVideoPlayer() {
      if (!this.hasVideo) {
        return;
      }

      this.loadComponent('video-player');
    },

    /**
     * Handle Splide mounted event.
     * @param {Object} splide - Splide instance.
     */
    handleSplideMounted() {
      // remove unwanted orientation from thumbnail slider
      this.removeAriaOrientation();
      // make sure the Aria label is set correctly
      this.restoreAriaLabel();
    },

    /**
     * Go to slide.
     * - Set speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     * @param {Boolean} animate - Animate slide transition.
     * @returns {Promise}
     */
    goToIndex(index) {
      return new Promise((resolve) => {
        if (
          !this.$refs.splide ||
          !this.carousel ||
          !this.carousel.Components?.Controller ||
          this.carousel.index === index
        ) {
          // Splide silently no-ops when the target index matches its current
          // index, so it never fires `moved` - skip setting isProgrammaticMove
          // here or it would get stuck `true` and block the next real emit.
          resolve();
          return;
        }

        this.isProgrammaticMove = true;

        setTimeout(() => {
          this.carousel.go(index);
        }, 0);

        resolve();
      });
    },

    /**
     * Get video active state.
     * - Play video when gallery is active and slide is visible.
     * @param {Number} index - Video media index.
     * @returns {Boolean}
     */
    getVideoActive(index) {
      return this.layout === 'grid'
        ? this.namespaceActive
        : this.namespaceActive && index === this.currentIndex;
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4');
    },

    zoomGallery() {
      // Update Splide carousel perPage setting
      if (this.isZoomed) {
        this.carousel.options = {
          perPage: 1.5,
        };

        this.isZoomed = false;
      } else {
        this.carousel.options = {
          perPage: 1,
        };

        this.isZoomed = true;
      }
    },

    /**
     * Handle image click.
     * @param {Number} index - Clicked media index.
     */
    handleImageClick(index) {
      // emit click to update carousel in parent
      this.$emit('update-current-index', index);
    },

    /**
     * Handle video state update.
     * @param {String} state - Video state ('loading', 'paused', 'playing', 'ready').
     */
    handleVideoStateUpdate(state) {
      // Zoom gallery when video starts playing on mobile
      if (state === 'playing' && this.isMobile && !this.isZoomed) {
        this.zoomGallery();
      }
    },

    /**
     * remove aria-orientation from splide__list
     */
    removeAriaOrientation() {
      if (this.type !== 'thumbnails') return;
      const root = this.carousel && this.carousel.root;
      if (!root) return;
      const list = root.querySelector('.splide__list');
      if (!list) return;
      list.removeAttribute('aria-orientation');
      // fallback in case splide adds it again
      requestAnimationFrame(() => {
        list.removeAttribute('aria-orientation');
      });
    },

    /**
     * Restores the aria-label after it has been remove from splide
     */
    restoreAriaLabel() {
      const root = this.carousel && this.carousel.root;
      if (!root) {
        return;
      }
      const desired = this.ariaLabel;
      if (desired && root.getAttribute('aria-label') !== desired) {
        root.setAttribute('aria-label', desired);
        // fallback after frame in case splide sets it again
        requestAnimationFrame(() => {
          if (root.getAttribute('aria-label') !== desired) {
            root.setAttribute('aria-label', desired);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import './product-gallery-carousel';
</style>
